import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { BookOpen, Clock, CheckCircle2, XCircle, ClipboardList, Mail, Inbox, CreditCard, FileText, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ClearTestDataButton from "@/components/admin/ClearTestDataButton";

const STATUS_STYLES = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
  approved: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Approved" },
  rejected: { color: "bg-rose-100 text-rose-800 border-rose-200", label: "Rejected" },
};
const PAYMENT_STATUS_STYLES = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending Verification" },
  verified: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Verified" },
  rejected: { color: "bg-rose-100 text-rose-800 border-rose-200", label: "Payment Rejected" },
  not_required: { color: "bg-slate-100 text-slate-700 border-slate-200", label: "No Payment Required" },
};

export default function EnrollmentRequestsPage() {
  const { user } = useAuth();
  const canApprove = hasPerm(user, "enrollment_request", "approve");
  const canReject = hasPerm(user, "enrollment_request", "reject");
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ requests: [], counts: { pending: 0, approved: 0, rejected: 0 } });
  const [busyId, setBusyId] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: "" });
  const [rejectPayDialog, setRejectPayDialog] = useState({ open: false, id: null, reason: "" });
  const [proofDialog, setProofDialog] = useState({ open: false, loading: false, data: null, request: null });

  const load = async (status = tab) => {
    try {
      const r = await api.get(`/admin/enrollment-requests`, { params: { status } });
      setData(r.data || { requests: [], counts: {} });
    } catch {
      toast.error("Could not load enrollment requests");
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/enrollment-requests/${id}/approve`);
      toast.success("Enrollment approved");
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    const { id, reason } = rejectDialog;
    if (!id) return;
    setBusyId(id);
    try {
      await api.post(`/admin/enrollment-requests/${id}/reject`, { reason });
      toast.success("Enrollment rejected");
      setRejectDialog({ open: false, id: null, reason: "" });
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    } finally {
      setBusyId(null);
    }
  };

  const verifyPayment = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/enrollment-requests/${id}/verify-payment`);
      toast.success("Payment has been verified successfully.");
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify payment");
    } finally {
      setBusyId(null);
    }
  };

  const rejectPayment = async () => {
    const { id, reason } = rejectPayDialog;
    if (!id) return;
    setBusyId(id);
    try {
      await api.post(`/admin/enrollment-requests/${id}/reject-payment`, { reason });
      toast.success("Payment proof has been rejected.");
      setRejectPayDialog({ open: false, id: null, reason: "" });
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject payment");
    } finally {
      setBusyId(null);
    }
  };

  const viewProof = async (request) => {
    setProofDialog({ open: true, loading: true, data: null, request });
    try {
      const r = await api.get(`/admin/enrollment-requests/${request.id}/proof`);
      setProofDialog({ open: true, loading: false, data: r.data, request });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load proof");
      setProofDialog({ open: false, loading: false, data: null, request: null });
    }
  };

  return (
    <div className="space-y-8" data-testid="enrollment-requests-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-2">
            <ClipboardList className="w-3.5 h-3.5" /> Enrollment Requests
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Review course enrollment requests</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">Approve or reject student requests to enroll in additional courses. Approval adds the course to the student's enrolled list and sends them a push notification.</p>
        </div>
        <ClearTestDataButton
          role={user?.role}
          title="Clear Approved + Rejected"
          description="Permanently deletes all Approved and Rejected enrollment request rows. Pending requests are kept."
          endpoint="/admin/enrollment-requests/clear-resolved"
          confirmPhrase="CLEAR"
          onCleared={() => load(tab)}
          dataTestId="clear-enrollment-resolved"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending {data.counts.pending ? <Badge className="ml-2 bg-amber-500 text-white">{data.counts.pending}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved ({data.counts.approved || 0})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected ({data.counts.rejected || 0})</TabsTrigger>
        </TabsList>

        {["pending", "approved", "rejected"].map((s) => (
          <TabsContent key={s} value={s} className="mt-5">
            {data.requests.length === 0 ? (
              <Card className="p-12 text-center" data-testid={`empty-${s}`}>
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <div className="text-sm text-slate-500">No {s} requests.</div>
              </Card>
            ) : (
              <Card className="p-0 border-slate-200 overflow-hidden">
                <ul className="divide-y divide-slate-100">
                  {data.requests.map((r) => {
                    const meta = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                    return (
                      <li key={r.id} className="p-5 flex items-start gap-4 flex-wrap" data-testid={`er-row-${r.id}`}>
                        <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-700 grid place-items-center shrink-0">
                          <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-[240px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-base text-slate-900">{r.course_name}</span>
                            <Badge variant="outline" className={meta.color + " border"}>{meta.label}</Badge>
                            {r.payment_status && PAYMENT_STATUS_STYLES[r.payment_status] && (
                              <Badge variant="outline" className={PAYMENT_STATUS_STYLES[r.payment_status].color + " border inline-flex items-center gap-1"} data-testid={`payment-badge-${r.id}`}>
                                <CreditCard className="w-3 h-3" /> {PAYMENT_STATUS_STYLES[r.payment_status].label}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-700 mt-1 font-medium">{r.student_name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" /> {r.student_email}
                          </div>
                          {r.payment_reference_number && (
                            <div className="text-xs text-slate-600 mt-1">
                              Payment Ref: <span className="font-mono">{r.payment_reference_number}</span>
                            </div>
                          )}
                          {r.payment_rejection_reason && (
                            <div className="text-xs text-rose-700 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Payment rejected: {r.payment_rejection_reason}
                            </div>
                          )}
                          {r.note && (
                            <div className="text-xs text-slate-600 mt-2 italic bg-slate-50 px-3 py-2 rounded border-l-2 border-indigo-300">
                              "{r.note}"
                            </div>
                          )}
                          {r.reject_reason && (
                            <div className="text-xs text-rose-700 mt-2">Reason given: {r.reject_reason}</div>
                          )}
                          <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Requested {(r.requested_at || "").slice(0, 19).replace("T", " ")}</span>
                            {r.payment_submitted_at && <span>· Paid {(r.payment_submitted_at || "").slice(0, 19).replace("T", " ")}</span>}
                            {r.reviewed_by_name && <span>· Reviewed by {r.reviewed_by_name}</span>}
                          </div>
                        </div>
                        {r.status === "pending" && (
                          <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                            {(r.proof_of_payment_filename || r.payment_reference_number) && (
                              <Button variant="outline" size="sm" onClick={() => viewProof(r)} data-testid={`view-proof-btn-${r.id}`}>
                                <Eye className="w-4 h-4 mr-1" /> View Proof
                              </Button>
                            )}
                            {r.payment_required && r.payment_status === "pending" && (
                              <>
                                <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                  disabled={!canReject || busyId === r.id}
                                  onClick={() => setRejectPayDialog({ open: true, id: r.id, reason: "" })}
                                  data-testid={`reject-payment-btn-${r.id}`}>
                                  Reject Payment
                                </Button>
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                                  disabled={!canApprove || busyId === r.id}
                                  onClick={() => verifyPayment(r.id)}
                                  data-testid={`verify-payment-btn-${r.id}`}>
                                  <CheckCircle2 className="w-4 h-4 mr-1" /> Verify Payment
                                </Button>
                              </>
                            )}
                            {(!r.payment_required || r.payment_status === "verified") && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                  disabled={!canReject || busyId === r.id}
                                  onClick={() => setRejectDialog({ open: true, id: r.id, reason: "" })}
                                  data-testid={`reject-btn-${r.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  disabled={!canApprove || busyId === r.id}
                                  onClick={() => approve(r.id)}
                                  data-testid={`approve-btn-${r.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approve Enrollment
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject enrollment request</DialogTitle>
            <DialogDescription>The student will be notified. Optionally provide a reason.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason <span className="text-slate-400 text-xs ml-1">optional ({rejectDialog.reason.length}/300)</span></Label>
            <Textarea
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value.slice(0, 300) })}
              placeholder="e.g. Course capacity reached"
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog({ open: false, id: null, reason: "" })}>Cancel</Button>
            <Button onClick={reject} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-confirm-btn">
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Payment dialog */}
      <Dialog open={rejectPayDialog.open} onOpenChange={(o) => setRejectPayDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="reject-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Reject Payment Proof
            </DialogTitle>
            <DialogDescription>
              The student will be notified and asked to upload valid proof of payment. Optionally include a reason.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason <span className="text-slate-400 text-xs ml-1">optional ({rejectPayDialog.reason.length}/300)</span></Label>
            <Textarea
              value={rejectPayDialog.reason}
              onChange={(e) => setRejectPayDialog({ ...rejectPayDialog, reason: e.target.value.slice(0, 300) })}
              placeholder="e.g. Reference number does not match transactions"
              rows={3}
              data-testid="reject-payment-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectPayDialog({ open: false, id: null, reason: "" })}>Cancel</Button>
            <Button onClick={rejectPayment} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-payment-confirm-btn">
              Reject Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof of Payment viewer */}
      <Dialog open={proofDialog.open} onOpenChange={(o) => setProofDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="proof-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Proof of Payment
            </DialogTitle>
            <DialogDescription>
              {proofDialog.request?.student_name} · {proofDialog.request?.course_name}
            </DialogDescription>
          </DialogHeader>
          {proofDialog.loading && (<div className="text-sm text-slate-500 py-6 text-center">Loading…</div>)}
          {proofDialog.data && (
            <div className="space-y-3">
              {proofDialog.data.payment_reference_number && (
                <div className="text-sm">
                  <span className="text-slate-500">Reference: </span>
                  <span className="font-mono text-slate-900">{proofDialog.data.payment_reference_number}</span>
                </div>
              )}
              {proofDialog.data.proof_of_payment_data_url ? (
                proofDialog.data.proof_of_payment_mime === "application/pdf" ? (
                  <>
                    <iframe
                      src={proofDialog.data.proof_of_payment_data_url}
                      title="Proof of payment"
                      className="w-full h-[480px] border rounded-md bg-white"
                    />
                    <a href={proofDialog.data.proof_of_payment_data_url} download={proofDialog.data.proof_of_payment_filename || "proof.pdf"}>
                      <Button variant="outline" size="sm"><FileText className="w-4 h-4 mr-1" /> Download PDF</Button>
                    </a>
                  </>
                ) : (
                  <img
                    src={proofDialog.data.proof_of_payment_data_url}
                    alt="Proof of payment"
                    className="max-w-full max-h-[480px] border rounded-md mx-auto bg-white"
                  />
                )
              ) : (
                <div className="text-sm text-slate-500 italic">No file uploaded — student submitted reference number only.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofDialog({ open: false, loading: false, data: null, request: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
